import { config } from "./config"
import { HeroUIProvider } from "@heroui/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import ProtectedRoute from "./routes/ProtectedRoute";
import { AuthStore, SessionStore } from "./stores";
import { setNavigate } from "./utils/navigationUtils";
import SessionExpiredModal from "./components/SessionExpiredModal";
import logo from "./assets/Aiquris Logo.png";
/* 🔥 Lazy-loaded pages (code splitting) */
const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/SignUp"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Settings = lazy(() => import("./pages/Settings"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ChatPage = lazy(() => import("./pages/Chat"));
import AppLayout from "./components/AppLayout";
import Impactplus from "./pages/impact-plus";
import { ErrorBoundary } from "./components/ErrorBoundary";




// Fail fast if runtime config wasn't injected (e.g. entrypoint script didn't run)
if (!config.API_BASE_URL || config.API_BASE_URL.includes('${')) {
  document.getElementById('root')!.innerHTML =
    '<pre>Configuration error: API_BASE_URL is not set. Check container logs.</pre>';
  throw new Error('Runtime config missing: API_BASE_URL');
}

console.info(`Platform Build: ${config.BUILD_TAG}`); // handy breadcrumb in browser console



function NavigationHandler({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  return <>{children}</>;
}

function RootRedirect() {
  const { isAuthenticated, hasHydrated } = AuthStore.useAuthStore();

  // Don't redirect until auth hydration is complete
  if (!hasHydrated) return null;
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

function getBasename() {
  const match = window.location.pathname.match(/^(\/use-case\/[^/]+)/);
  return match ? match[1] : "/";
}

function App() {
  const { isLoading, hasHydrated, hydrate } = AuthStore.useAuthStore();
  const { isSessionExpiredModalOpen, hideSessionExpiredModal } = SessionStore.useSessionStore();




  useEffect(() => {
    hydrate();
  }, [hydrate]);

  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <HeroUIProvider>
      <Router basename={getBasename()}>
        <NavigationHandler>

          {isLoading && (
            <div className="fixed inset-0 grid place-items-center bg-black/10 z-50">
              <div className="text-lg">Signing in…</div>
            </div>
          )}

          <Suspense
            fallback={
              <div className="min-h-screen flex items-center justify-center">
                <div><img src={logo} alt="AIQURIS Logo" className="mx-auto w-28" /></div>
              </div>
            }
          >
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<RootRedirect />} />

                <Route path="/login" element={<Login />} />

                <Route path="/signup" element={<SignUp />} />

                <Route path="/verify-email" element={<VerifyEmail />} />

                <Route path="/forgot-password" element={<ForgotPassword />} />

                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                  path="/impact-plus/*"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/risk-plus/*"
                  element={<Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Impactplus />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/chat"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ChatPage />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/chat/:id/:mode"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <ChatPage />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <AppLayout>
                        <Settings />
                      </AppLayout>
                    </ProtectedRoute>
                  }
                />

                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </Suspense>
        </NavigationHandler>
        <SessionExpiredModal
          isOpen={isSessionExpiredModalOpen}
          onClose={hideSessionExpiredModal}
        />
        <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} closeOnClick pauseOnHover />
      </Router>
    </HeroUIProvider>
  );
}

export default App;
